import type { CSSProperties } from 'react'
import { brandGradient, readableInkOn } from '@/lib/color'
import { describeOffer } from '@shared/offers'
import {
  DEFAULT_OFFER_PAGE_STYLE,
  isDefaultPageStyle,
  type Offer,
  type OfferPageStyle,
} from '@shared/model'

/**
 * Hur ett erbjudande ska se ut, uträknat en gång.
 *
 * Bläddringsläget och erbjudandevyn ska visa samma design – det är samma
 * kupong, bara i två storlekar. Låg uträkningen i den ena komponenten och en
 * kopia i den andra skulle de driva isär vid första ändringen, och köparen
 * skulle möta två olika kuponger för samma rabatt.
 */

export const FONT_CLASS: Record<OfferPageStyle['font'], string> = {
  display: 'font-display',
  sans: 'font-sans',
  serif: 'font-serif',
  mono: 'font-mono',
  condensed: 'font-condensed',
}

export const SCALE_CLASS: Record<OfferPageStyle['scale'], string> = {
  sm: 'text-2xl',
  md: 'text-4xl',
  lg: 'text-5xl',
}

/**
 * Rubrikens storlek, som alltid följer inställningen.
 *
 * Lång text måste krympa för att få plats, men tidigare låstes den till en
 * fast storlek – en rubrik över 42 tecken blev text-xl vad man än valde, och
 * inställningen såg trasig ut. Nu bestämmer längden hur många steg ned från
 * det valda värdet man hamnar, så Stor är alltid större än Liten.
 */
export function headlineSize(style: OfferPageStyle, text: string): string {
  const steps: string[] = ['text-lg', 'text-xl', 'text-2xl', 'text-3xl', 'text-4xl', 'text-5xl']
  const chosen = steps.indexOf(SCALE_CLASS[style.scale])
  const shrink = text.length > 42 ? 2 : text.length > 24 ? 1 : 0
  return steps[Math.max(0, chosen - shrink)]
}

export interface ResolvedOfferStyle {
  style: OfferPageStyle
  /** Företagets färdiga kupongbild, när de levererat en. */
  couponImage: string | null
  isCompanyColor: boolean
  isLira: boolean
  hasImage: boolean
  imageUrl: string | null | undefined
  headline: string
  /** Rubriken är skriven för hand, inte formulerad automatiskt ur rabatten. */
  customHeadline: boolean
  /** Bilden bär texten själv – då tiger vår. */
  silent: boolean
  /** Bilden visas hel i stället för beskuren. */
  contain: boolean
  onLight: boolean
  headlineColor: string
  eyebrowColor: string
  /** Färdig style-egenskap för ytan, oavsett om det är bild, färg eller lutning. */
  surface: CSSProperties | undefined
  /** Klasser för ytans bakgrund, när den inte sätts av surface. */
  surfaceClass: string
}

/**
 * Vad kupongen ska se ut som.
 *
 * En egen sida från företaget slår allt – den är kupongen rakt av. Annars
 * avgörs designen av erbjudandets eget val, företagets grafiska profil,
 * företagets färg och Lira, i den ordningen; se kommentaren i funktionen.
 */
export function resolveOfferStyle(
  offer: Offer,
  brandColor?: string | null,
): ResolvedOfferStyle {
  const couponImage = offer.couponImageUrl?.trim() || null

  /*
   * Vems design som gäller, i fallande ordning.
   *
   * 1. Erbjudandets egen – men bara om någon aktivt valt den. En orörd
   *    Lira-standard räknas som inget val, se isDefaultPageStyle.
   * 2. Företagets grafiska profil. Det är den som gör att man inte behöver
   *    ställa in varje erbjudande för sig.
   * 3. Företagets färg, för företag som inte fått en profil än.
   * 4. Lira.
   */
  const ownStyle = offer.pageStyle && !isDefaultPageStyle(offer.pageStyle) ? offer.pageStyle : null
  const style: OfferPageStyle =
    ownStyle ??
    offer.companyPageStyle ??
    (brandColor
      ? { ...DEFAULT_OFFER_PAGE_STYLE, background: 'company', textTone: readableInkOn(brandColor) }
      : DEFAULT_OFFER_PAGE_STYLE)

  const isCompanyColor = style.background === 'company' && Boolean(brandColor)
  const isLira = style.background === 'lira' || (style.background === 'company' && !brandColor)
  const hasImage =
    Boolean(couponImage) || (style.background === 'image' && Boolean(style.backgroundImageUrl))
  const imageUrl = couponImage ?? style.backgroundImageUrl

  /*
   * Rubriken är erbjudandets egen, oavsett vems design som gäller.
   *
   * Den hämtas därför från erbjudandet och inte från den stil som vann ovan –
   * annars skulle en egen rubrik försvinna i samma stund som erbjudandet
   * började följa företagets profil. Äldre erbjudanden har den i sin
   * pageStyle, och den läses därifrån även när designen inte längre gör det.
   */
  const headline =
    offer.headline?.trim() || offer.pageStyle?.headline?.trim() || describeOffer(offer.offerTerms)
  const contain = style.imageFit === 'contain'
  // En egen sida bär alltid sin text. Vår rubrik ovanpå den vore att skriva
  // över företagets design med vår egen formulering av samma sak.
  const silent = Boolean(couponImage) || (style.designCarriesText === true && hasImage)

  // Lira-varianten behåller sin gula rubrik; de andra styrs av textfärgen,
  // som väljs separat i admin just för att bakgrunden kan vara vad som helst.
  const onLight =
    (isCompanyColor ? readableInkOn(brandColor as string) === 'dark' : style.textTone === 'dark') &&
    !isLira

  const surface: CSSProperties | undefined = hasImage
    ? {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: contain ? 'contain' : 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // Fond bakom en bild som visas hel, så att kanterna inte blir ett
        // vitt hål. Företagets färg om vi har den.
        backgroundColor: contain ? (brandColor ?? '#131320') : undefined,
      }
    : isCompanyColor
      ? // Lutning och inte plan färg: en plan mättad yta läser som affisch, en
        // lutning läser som material. Samma regel som gäller Lirablå.
        { backgroundImage: brandGradient(brandColor as string) }
      : style.background === 'color' && style.backgroundColor
        ? { backgroundColor: style.backgroundColor }
        : undefined

  const surfaceClass = [
    isLira && 'bg-brand-surface',
    style.background === 'light' && 'bg-white',
    style.background === 'dark' && 'bg-[#131320]',
  ]
    .filter(Boolean)
    .join(' ')

  return {
    style,
    couponImage,
    isCompanyColor,
    isLira,
    hasImage,
    imageUrl,
    headline,
    customHeadline: Boolean(offer.headline?.trim() || offer.pageStyle?.headline?.trim()),
    silent,
    contain,
    onLight,
    headlineColor: isLira ? 'text-accent' : onLight ? 'text-[#131320]' : 'text-white',
    eyebrowColor: isLira ? 'text-accent' : onLight ? 'text-[#131320]/70' : 'text-white/80',
    surface,
    surfaceClass,
  }
}
